import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  concat,
  NormalizedCacheObject,
  InMemoryCacheConfig,
} from '@apollo/client';
import { EnvModel } from '../env/model';
import { AuthModel } from '../auth/model';

/** Custom caching options for Apollo - see https://www.apollographql.com/docs/react/caching/cache-field-behavior */
const cacheOptions: InMemoryCacheConfig = {
  typePolicies: {
    Query: {
      fields: {
        find: {
          keyArgs: ['query', 'filters'],
          merge(existing = {}, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },
    FindResults: {
      fields: {
        tracks: {
          keyArgs: ['pagination', 'sortOrder', 'sortMode'],
          merge: true,
        },
      },
    },
    Track: {
      keyFields: ['identity'],
    },
    Playlist: {
      keyFields: ['identity'],
    },
    Artist: {
      keyFields: ['slug'],
    },
    Release: {
      keyFields: ['slug'],
    },
    Tag: {
      keyFields: ['name'],
    },
  },
};

export class GraphQLModel {
  public client: ApolloClient<NormalizedCacheObject>;

  constructor(private env: EnvModel, private auth: AuthModel) {
    const httpLink = new HttpLink({ uri: this.env.graphqlUri });
    const link = concat(authMiddleware(this.auth), httpLink);

    if (env.isServer) {
      this.client = new ApolloClient({
        ssrMode: true,
        cache: new InMemoryCache(cacheOptions),
        link,
      });
    } else {
      // Hydrate the cache for any Apollo graphQL queries that have already run on the server
      const { initialApolloModel } = window as any;

      this.client = new ApolloClient({
        cache: new InMemoryCache(cacheOptions).restore(initialApolloModel),
        link,
      });
    }
  }
}

const authMiddleware = (model: AuthModel) =>
  new ApolloLink((operation, forward) => {
    if (model.token && model.user) {
      // add the authorization to the headers
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${model.token}`,
        },
      }));
    }

    return forward(operation);
  });
